import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';
import './style.css';
import EmojiPicker from 'emoji-picker-react';
import { ReactComponent as SmileEmoji } from '../../../assets/images/ChatImages/smile.svg';
import { ReactComponent as FileSharing } from '../../../assets/images/ChatImages/filesharing.svg';
import { ReactComponent as Hash } from '../../../assets/images/ChatImages/hash.svg';
import { markRead } from '../../../store/features/markRead';
import { fetchCannedMessages } from '../../../store/features/cannedMessagesApi';
import { useSocket } from '../../../SocketContext';
import { buildRoomSocketPayload } from '../../../utils/socketRoomId';

function normalizeShortcut(value) {
  if (!value) return '';
  const trimmed = String(value).trim().toLowerCase();
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/** Active `/shortcut` token at the end of the input (after start or whitespace). */
function getActiveSlashToken(value) {
  const match = String(value || '').match(/(^|\s)(\/[^\s]*)$/);
  return match ? match[2] : null;
}

function LiveChatCannedList({ items, loading, error, onSelect }) {
  return (
    <div
      className="dropdown-canned-tag"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600, color: '#061522' }}>
        Canned responses
      </div>
      <div className="dropdown-canned-msg-list">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <CircularProgress size={22} />
          </div>
        ) : error ? (
          <p style={{ padding: '12px 10px', margin: 0, fontSize: 13, color: '#D32F2F' }}>
            Could not load canned responses.
          </p>
        ) : items.length === 0 ? (
          <p style={{ padding: '12px 10px', margin: 0, fontSize: 13, color: '#8B97A2' }}>
            No canned responses found.
          </p>
        ) : (
          items.map((item) => {
            const groupLabel = Array.isArray(item.groups)
              ? item.groups.map((g) => g?.title).filter(Boolean).join(', ')
              : '';
            return (
              <ul key={item._id || item.id || item.shortcut}>
                <li
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(item);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                      >
                        {item.title || item.shortcut || 'Untitled'}
                      </span>
                      {item.content ? (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: '#667785',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.content}
                        </div>
                      ) : null}
                    </div>
                    {groupLabel ? (
                      <span style={{ fontSize: 11, color: '#667785', flexShrink: 0 }}>
                        {groupLabel}
                      </span>
                    ) : null}
                  </div>
                </li>
              </ul>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChatMessageInput(props) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [tagMenu, setTagMenu] = useState(false);
  const [shortcutFilter, setShortcutFilter] = useState('');
  const userData = useSelector((state) => state.loginToken);
  const {
    list: cannedMessages = [],
    loading: cannedMessagesLoading,
    error: cannedMessagesError,
  } = useSelector((state) => state.cannedMessages);
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const dropdownRef = useRef();
  const fileInputRef = useRef();
  const typingTimeoutRef = useRef(null);
  const openedBySlashRef = useRef(false);
  // The shared connection is the one that ran `setup` and joined the chat rooms;
  // a locally created socket never joined, so its typing events went nowhere.
  const chatSocket = useSocket();

  const emojiPickerRef = useRef(null);
  const emojiToggleRef = useRef(null);
  const {
    sendMessage, editedMessage, inputText, setDrafts, setInputText, fileImage, setFileImage, fileImageSrc, setFileImageSrc ,sendMessageHandler, selectUser, setCannedResponse,
    isPrivate,
    setIsPrivate,
    canSendPublicReply = true,
    variant,
  } = props;
  const isV2 = variant === "v2";

  const businessId = userData?.response?.user?.business_id;
  // Public reply is only for the assigned agent; others stay on internal notes.
  const isInternal = !!isPrivate || !canSendPublicReply;

  const filteredCannedMessages = useMemo(() => {
    if (!shortcutFilter) return cannedMessages;
    const query = normalizeShortcut(shortcutFilter);
    if (query === '/') {
      return cannedMessages;
    }
    return cannedMessages.filter((item) => {
      const shortcut = normalizeShortcut(item.shortcut);
      if (!shortcut) return false;
      return shortcut.startsWith(query) || shortcut.includes(query);
    });
  }, [cannedMessages, shortcutFilter]);

  const emitTyping = (event) => {
    const payload = buildRoomSocketPayload(selectUser);
    if (!payload || !chatSocket) return;
    chatSocket.emit(event, payload);
  };

  useEffect(() => {
    if (!tagMenu || !businessId) return;
    dispatch(fetchCannedMessages({ businessId }));
  }, [tagMenu, businessId, dispatch]);

  useEffect(() => {
    if (!isPrivate) return;
    setTagMenu(false);
    setShortcutFilter('');
    openedBySlashRef.current = false;
  }, [isPrivate]);

  useEffect(() => {
    if (editedMessage) {
      setInputText(editedMessage.text);
    }
  }, [editedMessage]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const slashToken = getActiveSlashToken(value);
    const hashRegex = /(^|\s)#$/;

    if (!isInternal && slashToken !== null) {
      openedBySlashRef.current = true;
      setShortcutFilter(slashToken);
      setTagMenu(true);
    } else {
      setShortcutFilter('');
      if (openedBySlashRef.current) {
        openedBySlashRef.current = false;
        setTagMenu(false);
      } else if (!isInternal && hashRegex.test(value)) {
        setTagMenu(true);
      } else if (isInternal) {
        setTagMenu(false);
      }
    }

    setInputText(value);
    if (value.length > 0  && !isInternal) {
      emitTyping("typing");

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        emitTyping("stop-typing");
      },3000); // 10 seconds timeout
    } else {
      // If the input is empty, stop the typing event immediately
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      emitTyping("stop-typing");
    }

    // Save the draft for the current chat
    setDrafts((prevDrafts) => ({
      ...prevDrafts,
      [selectUser.id]: value,
    }));

  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleMessageSelect = (message) => {
    const content = message?.content || message?.message || "";
    setInputText((prevText) => {
      const slashToken = getActiveSlashToken(prevText);
      if (slashToken !== null) {
        const idx = prevText.lastIndexOf(slashToken);
        if (idx !== -1) {
          return `${prevText.slice(0, idx)}${content}${prevText.slice(idx + slashToken.length)}`;
        }
      }

      const lastHashIndex = prevText.lastIndexOf('#');
      if (lastHashIndex !== -1) {
        return `${prevText.slice(0, lastHashIndex)}${content}${prevText.slice(lastHashIndex + 1)}`;
      }

      return `${prevText}${content}`;
    });

    openedBySlashRef.current = false;
    setShortcutFilter('');
    setTagMenu(false);

    setTimeout(() => {
      setTagMenu(false);
    }, 0);
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const pickShortcutMatch = (filterValue) => {
    if (!filterValue || filteredCannedMessages.length === 0) return null;
    const query = normalizeShortcut(filterValue);
    const exact = filteredCannedMessages.find(
      (item) => normalizeShortcut(item.shortcut) === query
    );
    if (exact) return exact;
    if (filteredCannedMessages.length === 1) return filteredCannedMessages[0];
    return null;
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFileImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
    
    e.target.value = '';
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSendMessage = () => {
    if (inputText.trim() !== '' || fileImage) {
      sendMessage('User', inputText);
      setInputText('');
      sendMessageHandler();
      
      setDrafts((prevDrafts) => ({
        ...prevDrafts,
        [selectUser.id]: '', 
      }));
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === ' ' && tagMenu && shortcutFilter) {
      const match = pickShortcutMatch(shortcutFilter);
      if (match) {
        e.preventDefault();
        handleMessageSelect(match);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      if (tagMenu && shortcutFilter) {
        const match = pickShortcutMatch(shortcutFilter);
        if (match) {
          e.preventDefault();
          handleMessageSelect(match);
          return;
        }
      }
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleToggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiClick = (emojiObject) => {
    setInputText((prevText) => prevText + emojiObject.emoji);
    setShowEmojiPicker(false);
  };
  
  const handleClickOutside = (event) => {
    const isOutsideEmojiPicker = emojiPickerRef.current && !emojiPickerRef.current.contains(event.target);
    const isOutsideEmojiToggle = emojiToggleRef.current && !emojiToggleRef.current.contains(event.target);

    const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

    if (isOutsideEmojiPicker && isOutsideEmojiToggle) {
      setShowEmojiPicker(false);
    }
    
    if (isOutsideDropdown) {
      openedBySlashRef.current = false;
      setShortcutFilter('');
      setTagMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMarkRead = async () => {
    const userId = userData?.response?.user?._id;

    if(selectUser?.roomUsers?.includes(userId) && selectUser?.id) {
      chatSocket?.emit("mark-read", selectUser.id);
      const response = await dispatch(
        markRead({
          requestHeaders: {
            roomId: selectUser.id,
            userId: userId,
          },
        })
      );
    }
  
  }

  return (
    <div
      className={`ChatMessageInput-Main-Container${isV2 ? " chatv2-composer" : ""}${
        isV2 && isInternal ? " chatv2-composer--internal" : ""
      }`}
      style={
        isV2
          ? {
              padding: 0,
              overflow: "visible",
              background: isInternal ? "#FFFBE6" : "#fff",
              border: `1px solid ${isInternal ? "#F3E1A6" : "#D5DCE1"}`,
              borderRadius: 8,
              boxShadow: isInternal
                ? "0 1px 2px 0 rgba(6, 21, 34, 0.05)"
                : "none",
            }
          : undefined
      }
    >
      {isV2 && (
        <div
          className="chatv2-composer-tabs"
          style={{
            background: isInternal ? "#FFF1B8" : "#fff",
            borderBottomColor: isInternal ? "#F3E1A6" : "#E5E9EC",
          }}
        >
          <button
            type="button"
            className={`chatv2-composer-tab${!isInternal ? " is-active" : ""}${
              !canSendPublicReply ? " is-disabled" : ""
            }`}
            disabled={!canSendPublicReply}
            title={
              canSendPublicReply
                ? undefined
                : "Only the assigned agent can send a public reply"
            }
            onClick={() => canSendPublicReply && setIsPrivate?.(false)}
          >
            Public Reply
          </button>
          <button
            type="button"
            className={`chatv2-composer-tab${isInternal ? " is-active" : ""}`}
            onClick={() => setIsPrivate?.(true)}
          >
            Internal Note
          </button>
        </div>
      )}
      <div
        className="ChatMessageInput-Container"
        style={
          isV2
            ? {
                padding: "12px 16px 0",
                background: isInternal ? "#FFFBE6" : "transparent",
              }
            : undefined
        }
      >
        {fileImageSrc && (
          <>
          <img src={fileImageSrc} alt="user01 Logo" style={{  height: '40px', borderRadius: '4px' }} />
          <a href="#" className="clearImg" onClick={() => {setFileImage(""); setFileImageSrc(""); }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-tabler icon-tabler-circle-minus"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="#838383"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <circle cx="12" cy="12" r="9"/>
                <line x1="9" y1="12" x2="15" y2="12" />
              </svg>
            </a>
            </>
        )}
        <textarea
          ref={inputRef}
          placeholder="Type your message ..."
          type="text"
          value={inputText}
          onKeyPress={handleKeyPress}
          onChange={handleInputChange}
          className="ChatInput"
          onFocus={handleMarkRead}
          style={isV2 ? { fontSize: 14, minHeight: 64, background: "transparent" } : undefined}
        />
      </div>
      <div className="EmojiPicker-Top" ref={emojiPickerRef}>
        {showEmojiPicker && (
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            disableAutoFocus
            native
          />
        )}
      </div>
      <div
        className="ChatMessage-Button-Container"
        style={
          isV2
            ? {
                padding: "12px 16px 16px",
                marginTop: 0,
                background: isInternal ? "#FFFBE6" : "transparent",
                borderRadius: "0 0 7px 7px",
              }
            : undefined
        }
      >
        <div className="ChatMessage-Emoji00">
          <p ref={emojiToggleRef} onClick={handleToggleEmojiPicker} >
            <SmileEmoji />
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          <p onClick={handleButtonClick}>
            <FileSharing />
          </p>
          {!isInternal && (
          <p
            ref={dropdownRef}
            onClick={(e) => {
              if (e.target !== e.currentTarget && e.target.closest?.(".dropdown-canned-tag")) {
                return;
              }
              openedBySlashRef.current = false;
              setShortcutFilter('');
              setTagMenu((open) => !open);
            }}
          >
            <Hash />
            {tagMenu && (
              <LiveChatCannedList
                items={filteredCannedMessages}
                loading={cannedMessagesLoading}
                error={cannedMessagesError}
                onSelect={handleMessageSelect}
              />
            )}
          </p>
          )}
        </div>
        <div>
          <button
            className="ChatInput-Button"
            type="button"
            onClick={handleSendMessage}
            style={
              isV2
                ? {
                    width: 128,
                    minWidth: 128,
                    maxWidth: 128,
                    height: 40,
                    padding: 0,
                    borderRadius: 8,
                    background: "#148DFD",
                    fontSize: 14,
                    fontWeight: 500,
                    boxSizing: "border-box",
                  }
                : undefined
            }
          >
            {editedMessage ? "Update" : isV2 ? "Send Message" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatMessageInput;
